
import React, { useCallback, useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEO } from '@/components/seo/SEO';
import { VideoMasonryGrid } from '@/components/derivatives/VideoMasonryGrid';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertCircle, Clapperboard, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useInfiniteDerivatives } from '@/hooks/useInfiniteDerivatives';
import { DerivativesFilterBar, ExpressionType } from '@/components/derivatives/DerivativesFilterBar';

// Local storage key for filter preference
const LS_FILTER_KEY = 'derivatives_expression_filter';

const DerivativesGrid = () => {
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const playlistId = searchParams.get('playlist');
  
  // Expression filter state with localStorage persistence
  const [expressionFilter, setExpressionFilter] = useState<ExpressionType>(() => {
    const saved = localStorage.getItem(LS_FILTER_KEY);
    if (saved && ['all', 'edit', 'react', 'acting', 'other'].includes(saved)) {
      return saved as ExpressionType;
    }
    return 'all';
  });
  
  // Save filter preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(LS_FILTER_KEY, expressionFilter);
  }, [expressionFilter]);
  
  // Use the infinite derivatives hook with 12 items initially
  const {
    derivatives,
    loading,
    error,
    hasMore,
    isFetchingMore,
    loadMore,
    loadMoreRef
  } = useInfiniteDerivatives({
    initialLimit: 12, // Changed from 24 to 12
    fetchType: playlistId ? 'playlist' : 'latest',
    id: playlistId || undefined,
    disableAutoLoading: true, // We'll handle load more manually
    expressionType: expressionFilter
  });

  // Handle manual "Load More" button click
  const handleLoadMore = useCallback(() => {
    console.log("Load more clicked. Current count:", derivatives.length, "hasMore:", hasMore);
    loadMore();
  }, [loadMore, derivatives.length, hasMore]);

  console.log("DerivativesGrid render:", { 
    derivativesLength: derivatives.length,
    loading,
    hasMore,
    isFetchingMore,
    expressionFilter,
    playlistId
  });

  return (
    <Layout>
      <SEO 
        title={playlistId ? t('derivatives.playlistCreations') : t('derivatives.title')}
        description={t('derivatives.description')} 
      />
      <div className="container mx-auto py-8 px-4">
        <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="max-w-[60%]">
            <h1 className="text-3xl font-bold mb-4 flex items-center">
              <Clapperboard className="h-7 w-7 mr-3" />
              {playlistId ? t('derivatives.playlistCreations') : t('derivatives.title')}
            </h1>
            <p className="text-muted-foreground mt-2">
              {language === 'fr' ? t('derivatives.frenchDescription') : t('derivatives.description')}
            </p>
          </div>
          <DerivativesFilterBar 
            expressionFilter={expressionFilter}
            setExpressionFilter={setExpressionFilter}
            className="mt-0 md:mt-0 flex-shrink-0"
          />
        </div>
        
        {/* Loading skeleton for initial load */}
        {loading && derivatives.length === 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-6">
            {Array.from({ length: 8 }).map((_, index) => (
              <Skeleton key={index} className="aspect-[9/16] rounded-lg" />
            ))}
          </div>
        )}

        {/* Error display */}
        {error && (
          <Alert variant="destructive" className="mt-6">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common.error')}</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Main content - show results if we have derivatives or if not loading */}
        {(derivatives.length > 0 || (!loading && !error)) && (
          <div className="mt-4">
            <VideoMasonryGrid 
              items={derivatives}
              isLoading={false}
              error={null}
              forceVerticalLayout={true}
              showLoadMore={false} // We handle load more manually
            />
            
            {/* Manual Load More button */}
            {hasMore && derivatives.length > 0 && (
              <div className="mt-8 flex justify-center">
                <Button
                  variant="default"
                  size="lg"
                  onClick={handleLoadMore}
                  className="min-w-[200px]"
                  disabled={isFetchingMore}
                  data-testid="load-more-button"
                >
                  {isFetchingMore ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.loading')}
                    </>
                  ) : (
                    t('common.showMore')
                  )}
                </Button>
              </div>
            )}
            
            {/* All items loaded message */}
            {!hasMore && derivatives.length > 0 && (
              <div className="mt-8 text-center text-muted-foreground">
                {t('derivatives.allItemsLoaded')}
              </div>
            )}
            
            {/* Intersection observer target for potential future auto-loading */}
            <div ref={loadMoreRef} className="h-4" />
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && derivatives.length === 0 && (
          <div className="text-center py-16 mt-4">
            <p className="text-muted-foreground mb-4">
              {expressionFilter !== 'all' 
                ? t('derivatives.noDerivativesForFilter') || `No ${expressionFilter} creations found` 
                : t('derivatives.noDerivatives')}
            </p>
            {expressionFilter !== 'all' && (
              <Button 
                variant="outline" 
                onClick={() => setExpressionFilter('all')}
              >
                {t('common.viewAll')}
              </Button>
            )}
            {playlistId && (
              <Button asChild className="ml-2">
                <Link to="/derivatives">{t('common.viewAll')}</Link>
              </Button>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DerivativesGrid;
